import type { CollectionSnapshot } from "@core/domains/collections/collections.types";
import type { assets } from "@repo/client";
import { Node } from "@tiptap/core";
import type { SuggestionProps } from "@tiptap/suggestion";
import autocomplete from "prosemirror-autocomplete";
import { SolidNodeViewRenderer } from "tiptap-solid";
import { getPromptContextFromEditor, insertCharacterCommand } from "../helpers";
import { MentionNode } from "./Mentions.render";

export const MENTION_INPUT_DATA_ATTR = "data-mention-input";
export type MentionItemType =
  | {
      type: "asset";
      associatedCollection: string;
      data: assets.AssetSnapshot;
    }
  | {
      type: "collection";
      associatedCollection: string;
      data: CollectionSnapshot;
    };
export type MentionsSuggestionProps = SuggestionProps<MentionItemType>;

export const MentionsExtension = Node.create({
  // Properties related to rendering the node
  name: "mentionNode",
  group: "inline",
  atom: true,
  inline: true,

  addAttributes() {
    return {
      type: "",
      associatedCollection: "",
      data: {},
    };
  },
  addNodeView() {
    return SolidNodeViewRenderer(MentionNode);
  },
  renderText({ node }) {
    const attrs = node.attrs as MentionItemType;
    const id = attrs.data.id;
    const name = attrs.type === "asset" ? attrs.data.originalFilename : attrs.data.label;
    return `@[${id}]"${name}"`;
  },
  renderHTML({ node }) {
    const attrs = node.attrs as MentionItemType;
    const id = attrs.data.id;
    const name = attrs.type === "asset" ? attrs.data.originalFilename : attrs.data.label;
    return ["span", {}, `@[${id}]"${name}"`];
  },
  addProseMirrorPlugins() {
    return autocomplete({
      triggers: [
        {
          trigger: "@",
          cancelOnFirstSpace: false,
          name: "mentionInput",
          decorationAttrs: {
            class:
              "rounded bg-gray-200 border border-solid border-gray-300 text-black dark:bg-gray-800 dark:border-gray-700 dark:text-white",
            [MENTION_INPUT_DATA_ATTR]: "true",
          },
        },
      ],
      reducer: (action) => {
        const context = getPromptContextFromEditor(this.editor);
        if (!context) return true;

        const { mentionsState } = context;
        mentionsState.autocompleteActionsHandler({ action, editor: this.editor });
        return true;
      },
    });
  },
});
